import { useArtifactReviewQueueIds } from "@/components/artifacts/artifactReviewQueue.ts";
import { markCanvasUpdateUnseen } from "@/components/canvas/canvasUpdateBadge.ts";
import { parseCanvasContent } from "@/components/canvas/canvasContent.ts";
import {
  clearCanvasScreenOverlay,
  markCanvasScreenOverlay,
  readCanvasScreenOverlay,
  useCanvasScreenOverlay,
} from "@/components/canvas/canvasScreenOverlay.ts";
import type { CanvasActionContextValue, PushResult } from "@/components/canvas/canvasContext.tsx";
import { readCalendarWindow } from "@/components/canvas/useSharedCalendarState.ts";
import { useSocket } from "@/contexts/SocketContext.tsx";
import { MAX_CANVAS_STACK_DEPTH } from "@/types/agenticUiState.ts";
import type {
  CanvasDisplayMode,
  CanvasState,
  SetThreadCanvasResp,
  ThreadCanvasResp,
} from "@/types/agenticUiState.ts";
import type { MessageViewContext } from "@/types/messageViewContext.ts";
import type { ServerToClientThreadEvents } from "@/types/socketIo.ts";
import {
  type QueryClient,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api } from "@/lib/http.ts";
import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useRef } from "react";

/** Query factory for a thread's dedicated canvas state. */
export const canvasQueries = {
  all: () => ["threadCanvas"] as const,
  detail: (id: number) =>
    queryOptions({
      queryKey: [...canvasQueries.all(), id] as const,
      queryFn: async ({ signal }) => {
        const response = await api.get<ThreadCanvasResp>(`/api/a/threads/${id}/canvas`, { signal });
        return response.data;
      },
      staleTime: 5 * 60 * 1000,
    }),
};

/**
 * Used only before `/canvas` has populated the query cache, so first render has
 * a stable pane state and functional setters can diff against a complete shape.
 */
const DEFAULT_CANVAS_STATE: CanvasState = { canvasStack: [], visibility: "open", display: "card" };

/** Stable empty stack for the resting/loading state, so consumers reading the
 *  returned `canvasStack` get the same reference across renders. */
const EMPTY_STACK: string[] = [];
type ThreadCanvasPatch = Partial<CanvasState>;

/** Value-equality for two canvas stacks (a missing stack reads as empty), so a
 *  fresh array with identical contents doesn't register as a change. */
const stacksEqual = (a: string[] | undefined, b: string[] | undefined): boolean => {
  const x = a ?? [];
  const y = b ?? [];
  return x.length === y.length && x.every((urn, i) => urn === y[i]);
};

/** The `useState`-shaped canvas setter: a next value, or an updater over the
 * current canvas. Callers spread in whatever fields they touch. */
export type CanvasSetter = Dispatch<SetStateAction<CanvasState>>;

/**
 * Mutation key for canvas writes, so a failed write can tell whether a newer one
 * is still in flight and will reconcile the cache on its own.
 */
const canvasMutationKey = (threadId: number) => ["setThreadCanvas", threadId] as const;

/** Read a thread's canvas from the dedicated query cache. */
export const readCanvasState = (queryClient: QueryClient, threadId: number): CanvasState => {
  return (
    queryClient.getQueryData<ThreadCanvasResp>(canvasQueries.detail(threadId).queryKey) ??
    DEFAULT_CANVAS_STATE
  );
};

/**
 * A thread's canvas setter that both patches the cache and persists to the
 * dedicated canvas endpoint. This fires only from explicit user interactions,
 * never for inbound server-driven updates.
 */
export const useSetCanvas = (threadId: number): CanvasSetter => {
  const queryClient = useQueryClient();
  const { mutate } = useSetThreadCanvas({ threadId });
  return useCallback(
    (update: SetStateAction<CanvasState>) => {
      const prev = readCanvasState(queryClient, threadId);
      const next = typeof update === "function" ? update(prev) : update;
      const patch: Partial<CanvasState> = {};
      // Value-compare the stack (a fresh array is reference-unequal every time),
      // so a no-op reset to the same item doesn't spuriously POST.
      if (!stacksEqual(next.canvasStack, prev.canvasStack))
        patch.canvasStack = next.canvasStack ?? [];
      if (next.visibility !== prev.visibility) patch.visibility = next.visibility;
      if (next.display !== prev.display) patch.display = next.display;
      if (Object.keys(patch).length === 0) return;
      mutate(patch);
    },
    [queryClient, threadId, mutate],
  );
};

/**
 * A thread's canvas: its reactive state (`canvasStack`, `isCanvasOpen`) plus a stable `canvasActions`.
 *
 * This is tailored to the canvas on the thread detail screen - different canvas screens have
 * different state requirements, so this isn't shared.
 *
 * This is a whole-object subscription — a consumer re-renders when content or visibility changes;
 * those re-renders are cheap (message items are `React.memo`). `canvasActions` keeps a stable identity
 * independent of the state, so it can back `CanvasActionContext` without
 * re-rendering distant consumers. Pass `enabled: false` where the canvas can't
 * render (gated off, mobile).
 */
export const useThreadCanvas = (
  threadId: number,
  { enabled = true }: { enabled?: boolean } = {},
): {
  canvasStack: string[];
  isCanvasOpen: boolean;
  /** The content is laid over the screen because the user presented it while the
   *  panel was collapsed. Mutually exclusive with `isCanvasOpen`. */
  isCanvasOverlaid: boolean;
  isFullscreen: boolean;
  canvasActions: CanvasActionContextValue;
} => {
  const { data } = useQuery({
    ...canvasQueries.detail(threadId),
    enabled: enabled && Number.isFinite(threadId),
  });
  const state = data ?? DEFAULT_CANVAS_STATE;

  const queryClient = useQueryClient();
  const setCanvas = useSetCanvas(threadId);

  /** An outside writer (chat link / feed chip): reset the stack to this one item.
   *  Declines (false) when the URN isn't canvas-renderable.
   *
   *  Presenting deliberately does not reveal a collapsed panel — that would
   *  restore a calendar the user chose to hide. The card is laid over the screen
   *  instead (see {@link markCanvasScreenOverlay}), so what they asked for still
   *  appears and the panel stays where they left it. */
  const resetTo = useCallback(
    (urn: string) => {
      if (!parseCanvasContent(urn)) return false;
      if (readCanvasState(queryClient, threadId).visibility !== "open") {
        markCanvasScreenOverlay(threadId, urn);
      }
      setCanvas((prev) => ({ ...prev, canvasStack: [urn] }));
      return true;
    },
    [queryClient, threadId, setCanvas],
  );

  /**
   * Drill down: layer a URN over the current top. See {@link PushResult}.
   *
   * Overlays a collapsed panel on the same terms as {@link resetTo}, and for the
   * same reason: a drill the user asked for that drew nothing because the panel
   * happens to be shut would read as a dead click. The overlay is keyed on the
   * stack's *root* rather than on `urn`, since that is the layer whose lifetime
   * it shares — a push deeper is still the same thing laid over the screen.
   */
  const push = useCallback(
    (urn: string): PushResult => {
      if (!parseCanvasContent(urn)) return "unsupported";
      const canvas = readCanvasState(queryClient, threadId);
      const stack = canvas.canvasStack ?? [];
      // A self-link to the current top would stack a pointless duplicate.
      if (stack.at(-1) === urn) return "duplicate";
      if (stack.length >= MAX_CANVAS_STACK_DEPTH) return "max-depth";
      if (canvas.visibility !== "open") markCanvasScreenOverlay(threadId, stack[0] ?? urn);
      setCanvas((prev) => ({ ...prev, canvasStack: [...(prev.canvasStack ?? []), urn] }));
      return "pushed";
    },
    [queryClient, threadId, setCanvas],
  );

  /** Pop the top layer, revealing the one beneath (or the resting calendar). */
  const pop = useCallback(
    () => setCanvas((prev) => ({ ...prev, canvasStack: (prev.canvasStack ?? []).slice(0, -1) })),
    [setCanvas],
  );

  /** Clear the whole stack, resting the base calendar without touching visibility. */
  const clear = useCallback(() => setCanvas((prev) => ({ ...prev, canvasStack: [] })), [setCanvas]);

  // The overlay lasts exactly as long as the root the user presented. Reconciled
  // from the state rather than in each action, because every way it can end looks
  // the same here: clear and pop empty the stack, a card reporting its target gone
  // drops it, and `thread_canvas:update` replaces it outright. That last one is
  // the reason this compares roots instead of counting content — a server push
  // must never inherit an overlay and put the agent's card on screen unasked.
  const stackRoot = (state.canvasStack ?? [])[0];
  const overlaidRoot = useCanvasScreenOverlay(threadId);
  const rootIsOverlaid = overlaidRoot !== undefined && stackRoot === overlaidRoot;
  useEffect(() => {
    if (overlaidRoot !== undefined && !rootIsOverlaid) clearCanvasScreenOverlay(threadId);
  }, [overlaidRoot, rootIsOverlaid, threadId]);

  /**
   * Drop every layer addressing `urn` — the card discovered its target is gone
   * (see {@link CanvasActionContextValue.dropContent}).
   *
   * Filters rather than pops because the dead layer need not be on top, and
   * removes all occurrences because a URN can legitimately appear twice (`push`
   * only rejects duplicating the *current* top) and both copies are equally
   * dead. `setCanvas` no-ops when nothing matched, so a card that reports the
   * same death twice costs no write.
   */
  const dropContent = useCallback(
    (urn: string) =>
      setCanvas((prev) => ({
        ...prev,
        canvasStack: (prev.canvasStack ?? []).filter((entry) => entry !== urn),
      })),
    [setCanvas],
  );

  /** Reveal the canvas panel, which hosts the content from here on. */
  const open = useCallback(() => {
    clearCanvasScreenOverlay(threadId);
    setCanvas((prev) => ({ ...prev, visibility: "open" }));
  }, [threadId, setCanvas]);
  /** Collapse the canvas panel. */
  const close = useCallback(
    () => setCanvas((prev) => ({ ...prev, visibility: "closed" })),
    [setCanvas],
  );
  /** Persistently select how the current canvas entity should be displayed. */
  const setDisplayMode = useCallback(
    (mode: CanvasDisplayMode) => setCanvas((prev) => ({ ...prev, display: mode })),
    [setCanvas],
  );

  // Read live from the cache (never a closed-over value) so a message carries the
  // freshest view at send time. `MessageViewContext` carries no visibility field
  // for the server to filter on, so gate here: a collapsed panel shows neither
  // the card nor the calendar — unless the content is overlaid on the screen, in
  // which case the user is looking at the card and the agent has to know, while
  // the calendar behind the panel stays genuinely hidden. `canvasItem` is the
  // stack top (the deictically primary thing on screen); `tagUiContext` projects
  // it and the calendar for the agent.
  const getSnapshot = useCallback((): MessageViewContext | undefined => {
    const canvas = readCanvasState(queryClient, threadId);
    if (canvas.visibility === "closed") {
      // Only while the overlaid root is still the one on the stack — otherwise
      // nothing of the user's is on screen and the panel really is empty.
      const stack = canvas.canvasStack ?? [];
      if (readCanvasScreenOverlay(threadId) !== stack[0]) return undefined;
      const canvasItem = stack.at(-1);
      return canvasItem ? { canvasItem } : undefined;
    }
    const stack = canvas.canvasStack ?? [];
    const canvasItem = stack.at(-1);
    // `display: "full"` is sticky across `clear()`, so only treat it as
    // calendar-hiding when a card is actually stacked; an empty stack still
    // rests on the visible calendar.
    const calendar =
      canvas.display === "full" && stack.length > 0 ? undefined : readCalendarWindow();
    if (!canvasItem && !calendar) return undefined;
    return { canvasItem, calendar };
  }, [queryClient, threadId]);

  const canvasActions = useMemo<CanvasActionContextValue>(
    () => ({
      resetTo,
      push,
      pop,
      clear,
      dropContent,
      open,
      close,
      setDisplayMode,
      getSnapshot,
    }),
    [resetTo, push, pop, clear, dropContent, open, close, setDisplayMode, getSnapshot],
  );

  return {
    canvasStack: state.canvasStack ?? EMPTY_STACK,
    isCanvasOpen: state.visibility === "open",
    isCanvasOverlaid: rootIsOverlaid && state.visibility !== "open",
    // Derive the button's boolean from the sticky display mode; the cache is
    // always seeded concrete via `DEFAULT_CANVAS_STATE` / the resolved response.
    isFullscreen: state.display === "full",
    canvasActions,
  };
};

/**
 * The URN a thread's canvas is presenting (its stack top), read reactively — a
 * read-only subscription for surfaces that need to *align with* the canvas
 * without driving it, so they don't pull in the whole writer handle.
 *
 * `undefined` when the stack is empty (resting on the bare calendar) or when
 * `enabled` is false (mobile / gate-off), where there's no canvas to align with.
 * Visibility is deliberately ignored: a collapsed panel still has a target, and a
 * surface that agrees with it now matches what the user sees on reopening.
 */
export const useCanvasStackTop = (
  threadId: number,
  { enabled = true }: { enabled?: boolean } = {},
): string | undefined => {
  const { data } = useQuery({
    ...canvasQueries.detail(threadId),
    enabled: enabled && Number.isFinite(threadId),
  });
  return (data?.canvasStack ?? EMPTY_STACK).at(-1);
};

/**
 * Write through a partial canvas patch. The dedicated canvas cache is the only
 * live source for the split pane; thread detail/list refetches cannot overwrite
 * it while a local write is pending.
 */
export const useSetThreadCanvas = ({ threadId }: { threadId: number }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: canvasMutationKey(threadId),
    // Preserve click order at the persistence boundary while still applying
    // every optimistic update immediately in `onMutate`.
    scope: { id: `thread-canvas-${threadId}` },
    mutationFn: async (canvas: ThreadCanvasPatch) => {
      const response = await api.post<SetThreadCanvasResp>(
        `/api/a/threads/${threadId}/canvas`,
        canvas,
      );
      return response.data;
    },
    onMutate: (canvas) => {
      const { queryKey } = canvasQueries.detail(threadId);
      // Abort any in-flight `/canvas` GET so its older response can't resolve after this
      // optimistic write and revert the user's action (mirrors `useThreadCanvasSocket`).
      void queryClient.cancelQueries({ queryKey });
      // Synchronous so the optimistic write lands before `mutate` returns — rapid
      // chained flips read each other's writes via `readCanvasState`.
      const snapshot = queryClient.getQueryData<ThreadCanvasResp>(queryKey);
      const optimistic: ThreadCanvasResp = {
        ...readCanvasState(queryClient, threadId),
        ...canvas,
      };
      queryClient.setQueryData<ThreadCanvasResp>(queryKey, optimistic);
      return { snapshot, optimistic };
    },
    onSuccess: (data, _canvas, context) => {
      queryClient.setQueryData<ThreadCanvasResp>(
        canvasQueries.detail(threadId).queryKey,
        (current) => {
          // Anything written after this mutation's optimistic value — a newer flip,
          // a `thread_canvas:update` — is newer than this response too. Otherwise
          // the response is canonical: it carries fields the write never patched,
          // which a cold cache could only fabricate from `DEFAULT_CANVAS_STATE`.
          if (
            !current ||
            !stacksEqual(current.canvasStack, context.optimistic.canvasStack) ||
            current.visibility !== context.optimistic.visibility ||
            current.display !== context.optimistic.display
          ) {
            return current;
          }
          return data.canvas;
        },
      );
    },
    onError: (error, canvas, context) => {
      console.error(`Failed to persist canvas for thread ${threadId}:`, error);
      // Roll back a field only while it still has this mutation's optimistic
      // value. A newer local write or `thread_canvas:update` wins instead.
      const snapshot = context?.snapshot ?? DEFAULT_CANVAS_STATE;
      queryClient.setQueryData<ThreadCanvasResp>(
        canvasQueries.detail(threadId).queryKey,
        (current) => {
          const base = current ?? DEFAULT_CANVAS_STATE;
          const optimistic = context?.optimistic;
          const shouldRollbackStack =
            canvas.canvasStack !== undefined &&
            optimistic !== undefined &&
            stacksEqual(base.canvasStack, optimistic.canvasStack);
          const shouldRollbackVisibility =
            canvas.visibility !== undefined &&
            optimistic !== undefined &&
            base.visibility === optimistic.visibility;
          const shouldRollbackDisplay =
            canvas.display !== undefined &&
            optimistic !== undefined &&
            base.display === optimistic.display;
          return {
            canvasStack: shouldRollbackStack ? snapshot.canvasStack : base.canvasStack,
            visibility: shouldRollbackVisibility ? snapshot.visibility : base.visibility,
            display: shouldRollbackDisplay ? snapshot.display : base.display,
          };
        },
      );
      // A write against a cold cache aborted the only GET that would have filled in
      // the fields it fabricated, and brought back no response to seed from — so
      // refetch what it discarded. A newer write still in flight will seed from its
      // own response, and refetching under it would revert it.
      if (
        context?.snapshot === undefined &&
        queryClient.isMutating({ mutationKey: canvasMutationKey(threadId) }) <= 1
      ) {
        void queryClient.invalidateQueries({ queryKey: canvasQueries.detail(threadId).queryKey });
      }
    },
    // No success-path invalidate: server-driven canvas targets arrive via
    // `thread_canvas:update`, and a refetch here would race that socket patch and
    // could revert a newer `canvasStack` the server just pushed. `onSuccess`
    // reconciles from the write's own response instead — same canonical state, no
    // extra read, and it defers to anything newer.
  });
};

/**
 * Whether the canvas is currently presenting an artifact that is part of a live
 * review — the case where a server-driven stack change would move the user off a
 * card they are still deciding.
 */
const isPresentingReviewedArtifact = (
  canvas: CanvasState,
  reviewIds: readonly string[],
): boolean => {
  if (reviewIds.length === 0) return false;
  const presented = (canvas.canvasStack ?? []).at(-1);
  const parsed = presented ? parseCanvasContent(presented) : null;
  return parsed?.kind === "artifact" && reviewIds.includes(parsed.id);
};

/**
 * Canvas socket side effects. Server-driven canvas updates patch the dedicated
 * canvas cache and badge the toggle.
 */
export const useThreadCanvasSocket = () => {
  const queryClient = useQueryClient();
  const { socket } = useSocket();
  // Latest-value ref so the handler reads the live review without re-subscribing
  // the socket every time a card is decided.
  const reviewIds = useArtifactReviewQueueIds();
  const reviewIdsRef = useRef(reviewIds);
  reviewIdsRef.current = reviewIds;

  useEffect(() => {
    if (!socket) return;

    // Intentionally NOT routed through `useThrottledInvalidation` like the
    // calendar and thread-list socket hooks. Those coalesce `invalidateQueries`
    // — an idempotent "refetch next time" that loses nothing when N events
    // collapse into one. This handler instead does `setQueryData` and makes no
    // network request, so it can't trip the rate limiter. More to the point, each
    // payload is a *partial* diff merged onto the previous state below, so a
    // last-payload-wins coalescer would permanently drop the fields only the
    // intermediate diffs carried — incorrect canvas state, not merely stale.
    const handleCanvasUpdate = (
      eventData: Parameters<ServerToClientThreadEvents["thread_canvas:update"]>[0],
    ) => {
      const { threadId, canvas } = eventData;
      // Abort any in-flight `/canvas` GET (via its AbortSignal) so its older
      // response can't resolve after this patch and revert the server-driven
      // target. Fire-and-forget: the abort is synchronous, and keeping the
      // handler synchronous avoids reordering concurrent updates behind an await.
      void queryClient.cancelQueries({ queryKey: canvasQueries.detail(threadId).queryKey });
      const prevCanvas = readCanvasState(queryClient, threadId);
      const next: ThreadCanvasResp = { ...prevCanvas, ...canvas };
      // Deciding a card starts an agent run, and a run auto-presents its first
      // artifact by resetting the stack — which would take the canvas away from
      // a user who is still working through a review, on their own click
      // (BIG-7171). Their card stays; the badge below still tells them something
      // arrived, and the toggle takes them to it once they're done.
      if (canvas.canvasStack && isPresentingReviewedArtifact(prevCanvas, reviewIdsRef.current)) {
        next.canvasStack = prevCanvas.canvasStack;
      }
      queryClient.setQueryData<ThreadCanvasResp>(canvasQueries.detail(threadId).queryKey, next);
      markCanvasUpdateUnseen(threadId);
    };

    socket.on("thread_canvas:update", handleCanvasUpdate);

    return () => {
      socket.off("thread_canvas:update", handleCanvasUpdate);
    };
  }, [socket, queryClient]);
};
